import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Flame } from "lucide-react";
import { BACKEND_URL } from "@/lib/env";

const API = `${BACKEND_URL}/api`;

export default function PublicFunnel() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/funnel/${slug}`, {
          timeout: 15000,
        });
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (e) {
        const status = e?.response?.status;
        const msg = status === 404 ? "not_found" : e?.message || "load_error";
        if (!cancelled) setState({ loading: false, error: msg, data: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state.loading) {
    return (
      <div
        className="cockpit-bg min-h-screen w-full flex items-center justify-center"
        data-testid="funnel-loading"
      >
        <span className="typing">
          <span />
          <span />
          <span />
        </span>
      </div>
    );
  }

  if (state.error === "not_found") {
    return (
      <div
        className="cockpit-bg min-h-screen w-full flex items-center justify-center px-6"
        data-testid="funnel-not-found"
      >
        <div className="text-center">
          <div className="w-14 h-14 rounded-md bg-[#FF006E]/15 border border-[#FF006E]/40 mx-auto mb-4 flex items-center justify-center">
            <Flame size={22} strokeWidth={1.7} className="text-[#FF006E]" />
          </div>
          <h1 className="font-display text-2xl font-semibold mb-2">
            @{slug} not found
          </h1>
          <p className="text-zinc-500 text-sm">
            This page doesn&rsquo;t exist yet.
          </p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div
        className="cockpit-bg min-h-screen w-full flex items-center justify-center px-6"
        data-testid="funnel-error"
      >
        <p className="text-zinc-500 text-sm">Failed to load: {state.error}</p>
      </div>
    );
  }

  const { display_name, bio, avatar_url, links } = state.data || {};

  return (
    <div
      className="cockpit-bg min-h-screen w-full flex justify-center px-4 py-14"
      data-testid="public-funnel"
    >
      <div className="w-full max-w-md">
        {/* Profile */}
        <div className="text-center mb-10">
          <div
            className="w-24 h-24 mx-auto mb-5 rounded-full overflow-hidden border border-white/10 bg-white/5 flex items-center justify-center"
            data-testid="funnel-avatar"
          >
            {avatar_url ? (
              <img
                src={avatar_url}
                alt={display_name || slug}
                className="w-full h-full object-cover"
              />
            ) : (
              <Flame size={30} strokeWidth={1.6} className="text-[#FF006E]" />
            )}
          </div>
          <h1
            className="font-display text-3xl font-semibold tracking-tight"
            data-testid="funnel-display-name"
          >
            {display_name || `@${slug}`}
          </h1>
          <p
            className="text-zinc-400 text-sm mt-1"
            data-testid="funnel-handle"
          >
            @{slug}
          </p>
          {bio && (
            <p
              className="text-zinc-300 text-[15px] leading-relaxed mt-5 whitespace-pre-wrap"
              data-testid="funnel-bio"
            >
              {bio}
            </p>
          )}
        </div>

        {/* Links */}
        <div className="space-y-3" data-testid="funnel-links">
          {(links || []).length === 0 ? (
            <p
              className="text-center text-zinc-500 text-sm"
              data-testid="funnel-links-empty"
            >
              No links yet.
            </p>
          ) : (
            (links || []).map((link) => (
              <a
                key={link.id}
                href={`${API}/r/${link.id}`}
                rel="noopener noreferrer"
                className="funnel-link group"
                data-testid={`funnel-link-${link.id}`}
              >
                <span className="funnel-link-icon">
                  {link.icon || "→"}
                </span>
                <span className="funnel-link-title">{link.title}</span>
              </a>
            ))
          )}
        </div>

        {/* Footer */}
        <p
          className="text-center text-[10px] text-zinc-600 uppercase tracking-[0.2em] mt-12"
          data-testid="funnel-footer"
        >
          persona dm cockpit
        </p>
      </div>
    </div>
  );
}
